import { Form } from 'factorial-form'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
  ContractsContractPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
} from 'generated/resources/graphql'

import {
  endsOnField,
  hasTrialPeriodField,
  jobTitleField,
  salaryFields,
  trialPeriodEndDateField,
  workingHoursFields,
} from 'modules/contracts/lib/checkFieldVisibility'
import { customFieldsFormKeys, customFieldsToFormValues } from 'modules/contracts/lib/customFields'

const contractVersionToFormValues = (
  contractVersion: ContractsEmployeesReferenceContractFragment | undefined | null,
  employeeId: string,
  payrollPolicyId?: string
) => {
  // Helper to convert null to undefined for number fields (factorial-form doesn't accept null for number fields)
  const toNumberOrUndefined = (value: number | null | undefined): number | undefined => {
    return value ?? undefined
  }

  // Helper to convert null to undefined for string ID fields (factorial-form doesn't accept null for string fields)
  const toStringOrUndefined = (value: string | null | undefined): string | undefined => {
    return value ?? undefined
  }

  return {
    // Base fields
    action_type: contractVersion?.actionType,
    adjusted_daily_minutes: contractVersion?.adjustedDailyMinutes,
    annual_workdays_count: contractVersion?.annualWorkdaysCount,
    annual_working_time_distribution: contractVersion?.annualWorkingTimeDistribution,
    bank_holiday_treatment: contractVersion?.bankHolidayTreatment,
    country: contractVersion?.country,
    created_at: contractVersion?.createdAt,
    created_by_avatar: contractVersion?.createdByAvatar,
    created_by_name: contractVersion?.createdByName,
    effective_on: null,
    employee_id: employeeId,
    ends_on: null,
    has_payroll: contractVersion?.hasPayroll ?? false,
    has_trial_period: contractVersion?.hasTrialPeriod ?? false,
    id: toStringOrUndefined(contractVersion?.id),
    is_discontinuous: false,
    starts_on: null,
    trial_period_ends_on: contractVersion?.trialPeriodEndsOn,
    updated_at: contractVersion?.updatedAt,

    // Job catalog
    job_catalog_level_id: toStringOrUndefined(contractVersion?.jobCatalogLevel?.id),
    job_catalog_role: contractVersion?.jobCatalogLevel?.role?.name,
    job_catalog_role_level_name: contractVersion?.jobCatalogLevel?.name,
    job_catalog_role_id: toStringOrUndefined(contractVersion?.jobCatalogLevel?.role?.id),
    job_catalog_tree_node_uuid: contractVersion?.jobCatalogTreeNodeUuid,
    job_title: contractVersion?.jobTitle,

    // Working hours
    // These are 'cents' type fields which also don't accept null - convert to undefined
    max_legal_yearly_hours: toNumberOrUndefined(contractVersion?.maxLegalYearlyHours),
    max_work_minutes_per_day: toNumberOrUndefined(contractVersion?.maxWorkMinutesPerDay),
    maximum_weekly_hours: toNumberOrUndefined(contractVersion?.maximumWeeklyHours),
    min_rest_minutes_between_days: toNumberOrUndefined(contractVersion?.minRestMinutesBetweenDays),
    working_hours: toNumberOrUndefined(contractVersion?.workingHours),
    working_hours_frequency: contractVersion?.workingHoursFrequency,
    working_time_percentage_in_cents: toNumberOrUndefined(
      contractVersion?.workingTimePercentageInCents
    ),
    working_week_days: contractVersion?.workingWeekDays,

    // Salary
    // We need to convert the salary amount to a number because the form field is typed as a number
    // and the graphql salaryAmount is typed as BigInt and returning a string
    salary_amount: contractVersion?.salaryAmount ? Number(contractVersion.salaryAmount) : undefined,
    salary_frequency: contractVersion?.salaryFrequency,

    // Payroll
    payroll_policy_id: toStringOrUndefined(payrollPolicyId),

    // Germany (DE)
    de_activity_type: toNumberOrUndefined(contractVersion?.deActivityType),
    de_contract_type_id: toStringOrUndefined(contractVersion?.deContractTypeId),
    de_employment_type: toNumberOrUndefined(contractVersion?.deEmploymentType),
    de_flat_rate_tax: toNumberOrUndefined(contractVersion?.deFlatRateTax),
    de_personal_key_group_id: toStringOrUndefined(contractVersion?.dePersonalKeyGroupId),
    de_personal_key_group_name: contractVersion?.dePersonalKeyGroupName,
    de_base_salary_type_id: toStringOrUndefined(contractVersion?.deBaseSalaryTypeId),

    // Spain (ES)
    es_a3innuva_job_position_id: toStringOrUndefined(contractVersion?.esA3innuvaJobPositionId),
    es_agreement_code_id: toStringOrUndefined(contractVersion?.esAgreementCodeId),
    es_classification_id: toStringOrUndefined(contractVersion?.esClassificationId),
    es_cno_occupation_id: toStringOrUndefined(contractVersion?.esCnoOccupationId),
    es_contract_observations: contractVersion?.esContractObservations,
    es_contract_type_id: toStringOrUndefined(contractVersion?.esContractTypeId),
    es_contribution_type_id: toStringOrUndefined(contractVersion?.esContributionTypeId),
    es_cotization_group: contractVersion?.esCotizationGroup,
    es_education_level_id: toStringOrUndefined(contractVersion?.esEducationLevelId),
    es_has_teleworking_contract: contractVersion?.esHasTeleworkingContract ?? false,
    es_job_description: contractVersion?.esJobDescription,
    es_occupation_code_id: toStringOrUndefined(contractVersion?.esOccupationCodeId),
    es_professional_category_id: toStringOrUndefined(contractVersion?.esProfessionalCategoryId),
    es_regime_id: toStringOrUndefined(contractVersion?.esRegimeId),
    es_tariff_group_id: contractVersion?.esTariffGroupId,
    es_tariff_group_name: contractVersion?.esTariffGroupName,
    es_working_day_type_id: toStringOrUndefined(contractVersion?.esWorkingDayTypeId),

    // France (FR)
    fr_coefficient: contractVersion?.frCoefficient,
    fr_contract_type_id: toStringOrUndefined(contractVersion?.frContractTypeId),
    fr_employee_type: contractVersion?.frEmployeeType,
    fr_forfait_jours: contractVersion?.frForfaitJours ?? false,
    fr_jours_par_an: toNumberOrUndefined(contractVersion?.frJoursParAn),
    fr_jours_par_an_cents: toNumberOrUndefined(contractVersion?.frJoursParAnCents),
    fr_level_id: toStringOrUndefined(contractVersion?.frLevelId),
    fr_mutual_id: toStringOrUndefined(contractVersion?.frMutualId),
    fr_professional_category_id: toStringOrUndefined(contractVersion?.frProfessionalCategoryId),
    fr_step_id: toStringOrUndefined(contractVersion?.frStepId),
    fr_work_type_id: toStringOrUndefined(contractVersion?.frWorkTypeId),

    // Portugal (PT)
    pt_contract_type_id: toStringOrUndefined(contractVersion?.ptContractTypeId),
  }
}

interface Props {
  contractVersion: ContractsEmployeesReferenceContractFragment | undefined | null
  employeeId: string
  customFields: ContractFlowCustomFieldFragment[]
  customValues: ContractFlowCustomFieldValueFragment[]
  contractContextlessPolicies: ContractsContractPoliciesFragment
  payrollPolicyId?: string
}

export const getUpdateFlowForm = ({
  contractVersion,
  employeeId,
  customFields,
  customValues,
  contractContextlessPolicies,
  payrollPolicyId,
}: Props): Form => {
  const canSeeJobTitle =
    contractVersion?._policies?.seeJobTitle.can ??
    contractContextlessPolicies.contractSeeJobTitle.can ??
    false
  const canSeeGrossSalary =
    contractVersion?._policies?.seeGrossSalary.can ??
    contractContextlessPolicies.contractSeeGrossSalary.can ??
    false
  const canSeeWorkingHours =
    contractVersion?._policies?.seeWorkingHours.can ??
    contractContextlessPolicies.contractSeeWorkingHours.can ??
    false
  const canSeeTrialPeriod =
    contractVersion?._policies?.seeTrialPeriod.can ??
    contractContextlessPolicies.contractSeeTrialPeriod.can ??
    false
  const canSeeTrialPeriodEndDate =
    contractVersion?._policies?.seeTrialPeriodEndDate.can ??
    contractContextlessPolicies.contractSeeTrialPeriodEndDate.can ??
    false
  const canSeeEndDate =
    contractVersion?._policies?.seeEndDate.can ??
    contractContextlessPolicies.contractSeeEndDate.can ??
    false

  return new Form(
    {
      ...contractVersionToFormValues(contractVersion, employeeId, payrollPolicyId),
      ...customFieldsToFormValues(customFields, customValues),
    },
    {
      ...jobTitleField(canSeeJobTitle),
      id: 'string',
      effective_on: 'date',
      starts_on: 'date',
      ends_on: 'date',
      bank_holiday_treatment: 'string',
      working_time_percentage_in_cents: 'number',
      annual_working_time_distribution: 'string',
      es_contract_type_id: 'string',
      es_contract_type_payroll_integration_code: 'string',
      es_contract_type_payroll_integration_name: 'string',
      es_contribution_type_id: 'string',
      es_contribution_type_payroll_integration_code: 'string',
      es_contribution_type_payroll_integration_name: 'string',
      es_cotization_group: 'string',
      es_education_level_id: 'string',
      es_agreement_code_id: 'string',
      es_agreement_code_payroll_integration_code: 'string',
      es_agreement_code_payroll_integration_name: 'string',
      es_education_level_payroll_integration_code: 'string',
      es_education_level_payroll_integration_name: 'string',
      es_cno_occupation_id: 'string',
      es_cno_occupation_payroll_integration_code: 'string',
      es_cno_occupation_payroll_integration_name: 'string',
      es_contract_observations: 'string',
      es_has_teleworking_contract: 'boolean',
      es_professional_category_id: 'string',
      es_job_description: 'string',
      es_working_day_type_id: 'string',
      is_discontinuous: 'boolean',
      es_regime_id: 'string',
      es_regime_payroll_integration_code: 'string',
      es_regime_payroll_integration_name: 'string',
      es_tariff_group_id: 'string',
      es_tariff_group_name: 'string',
      es_occupation_code_id: 'string',
      es_occupation_code_payroll_integration_code: 'string',
      es_occupation_code_payroll_integration_name: 'string',
      es_classification_id: 'string',
      es_classification_payroll_integration_code: 'string',
      es_classification_payroll_integration_name: 'string',
      es_a3innuva_job_position_id: 'string',
      es_a3innuva_job_position_payroll_integration_code: 'string',
      es_a3innuva_job_position_payroll_integration_name: 'string',
      fr_contract_type_id: 'string',
      fr_employee_type: 'string',
      fr_forfait_jours: 'boolean',
      fr_jours_par_an: 'number',
      fr_jours_par_an_cents: 'cents',
      fr_level_id: 'string',
      fr_mutual_id: 'string',
      fr_professional_category_id: 'string',
      fr_step_id: 'string',
      fr_work_type_id: 'string',
      fr_coefficient: 'string',
      de_contract_type_id: 'string',
      pt_contract_type_id: 'string',
      job_catalog_level_id: 'string',
      job_catalog_role_id: 'string',
      job_catalog_tree_node_uuid: 'string',
      job_catalog_role_level_name: 'string',
      payroll_policy_id: 'string',
      de_employment_type: 'number',
      de_flat_rate_tax: 'number',
      de_activity_type: 'number',
      de_personal_key_group_id: 'string',
      de_base_salary_type_id: 'string',
      ...salaryFields(canSeeGrossSalary),
      ...workingHoursFields(canSeeWorkingHours),
      ...hasTrialPeriodField(canSeeTrialPeriod),
      ...trialPeriodEndDateField(canSeeTrialPeriodEndDate),
      ...endsOnField(canSeeEndDate),
      ...customFieldsFormKeys(customFields, customValues),
    }
  )
}
